import React from "react";
import { useTranslation } from "react-i18next";
import { CustomColorPickerBottomSheet } from "components/colorpicker";
import type { TBuilderToolbarMenuViewPresentation } from "components/builderShell/BuilderToolbarMenuView";
import { BuilderToolbarMenuView } from "components/builderShell/BuilderToolbarMenuView";
import type { IBuilderToolbarMenuViewSection } from "components/builderShell/types";
import "components/builderShell/builderToolbarMenuView.scss";
import { selectDocument } from "../../../domain/selectors";
import { useEditorActions, useEditorStore } from "../../../domain/redux/hooks";
import type { EmailSettings } from "../../../domain/types";
import {
  EmailBuilderColorContrastHint,
  type EmailBuilderContrastHintPreset,
} from "../../../settings/EmailBuilderColorContrastHint";
import { useEmailBuilderSettingsOverlayPortalId } from "../../canvas/settings/emailBuilderSettingsOverlayPortal";
import { useEmailBuilderContinuousHistoryGroup } from "../blockSettings/emailBuilderContinuousHistoryGroup";
import { emailBuilderSettingsAutosaveGestureHandlers } from "../../../emailBuilderSettingsTextAutosaveDeferral";
import "../blockSettings/emailBuilderBlockSettings.scss";

type TDefaultColorKey =
  | "pageBackgroundColor"
  | "contentBackgroundColor"
  | "headingColor"
  | "paragraphColor"
  | "linkColor"
  | "buttonFillColor"
  | "buttonTextColor"
  | "buttonBorderColor"
  | "dividerColor";

interface IDefaultColorRow {
  key: TDefaultColorKey;
  label: string;
}

interface IDefaultColorSection {
  title: string;
  rows: IDefaultColorRow[];
}

const DEFAULT_COLOR_SECTIONS: IDefaultColorSection[] = [
  {
    title: "Background",
    rows: [
      { key: "pageBackgroundColor", label: "Page" },
      { key: "contentBackgroundColor", label: "Content" },
    ],
  },
  {
    title: "Text",
    rows: [
      { key: "headingColor", label: "Headings" },
      { key: "paragraphColor", label: "Paragraphs" },
      { key: "linkColor", label: "Links" },
    ],
  },
  {
    title: "Button",
    rows: [
      { key: "buttonFillColor", label: "Button fill" },
      { key: "buttonTextColor", label: "Button text" },
      { key: "buttonBorderColor", label: "Button border" },
    ],
  },
  {
    title: "Other",
    rows: [{ key: "dividerColor", label: "Divider" }],
  },
];

interface IEmailBuilderDefaultColorPickerRowProps {
  label: string;
  color: string;
  onChange: (color: string) => void;
  portalParentId?: string;
  contrastAgainst?: string;
  contrastLargeText?: boolean;
  contrastPreset?: EmailBuilderContrastHintPreset;
}

function defaultColorContrast(
  key: TDefaultColorKey,
  settings: EmailSettings,
): Pick<
  IEmailBuilderDefaultColorPickerRowProps,
  "contrastAgainst" | "contrastLargeText" | "contrastPreset"
> {
  switch (key) {
    case "headingColor":
    case "paragraphColor":
    case "linkColor":
      return {
        contrastAgainst: settings.contentBackgroundColor,
        contrastPreset: "content-background",
      };
    case "buttonTextColor":
      return {
        contrastAgainst: settings.buttonFillColor,
        contrastPreset: "button-fill",
      };
    default:
      return {};
  }
}

function EmailBuilderDefaultColorPickerRow(props: IEmailBuilderDefaultColorPickerRowProps) {
  const {
    label,
    color,
    onChange,
    portalParentId,
    contrastAgainst,
    contrastLargeText = false,
    contrastPreset = "content-background",
  } = props;
  const continuousHistory = useEmailBuilderContinuousHistoryGroup();
  const autosaveGestureHandlers = emailBuilderSettingsAutosaveGestureHandlers(continuousHistory);

  function handleColorChange(nextColor: string | null) {
    if (nextColor === null) {
      return;
    }
    continuousHistory.noteChange();
    onChange(nextColor);
  }

  return (
    <div className="email-builder-settings-color-field">
      <CustomColorPickerBottomSheet
        title={label}
        hexColor={color}
        updateHexColor={handleColorChange}
        portalParentId={portalParentId}
        fullWidth
        {...autosaveGestureHandlers}
      />
      <EmailBuilderColorContrastHint
        color={color}
        contrastAgainst={contrastAgainst}
        largeText={contrastLargeText}
        preset={contrastPreset}
      />
    </div>
  );
}

interface IEmailBuilderDefaultColorsToolbarMenuProps {
  presentation?: TBuilderToolbarMenuViewPresentation;
  panelTitle?: string;
  goBackToParentView?: () => void;
  onClose?: () => void;
  closeAriaLabel?: string;
}

export function EmailBuilderDefaultColorsToolbarMenu(
  props: IEmailBuilderDefaultColorsToolbarMenuProps,
) {
  const { t } = useTranslation();
  const presentation = props.presentation ?? "desktop-panel";
  const overlayPortalId = useEmailBuilderSettingsOverlayPortalId();
  const document = useEditorStore(selectDocument);
  const { updateSettings } = useEditorActions();
  const settings = document.settings;

  function handleColorChange(key: TDefaultColorKey, color: string | null) {
    if (color === null) {
      return;
    }
    updateSettings({ [key]: color } as Partial<EmailSettings>);
  }

  const sections: IBuilderToolbarMenuViewSection[] = DEFAULT_COLOR_SECTIONS.map(
    section => ({
      title: t(section.title),
      content: (
        <div className="email-builder-block-settings">
          {section.rows.map(row => (
            <EmailBuilderDefaultColorPickerRow
              key={row.key}
              label={t(row.label)}
              color={settings[row.key]}
              onChange={color => handleColorChange(row.key, color)}
              portalParentId={overlayPortalId}
              {...defaultColorContrast(row.key, settings)}
            />
          ))}
        </div>
      ),
    }),
  );

  return (
    <BuilderToolbarMenuView
      title={props.panelTitle ?? t("Default Colors")}
      presentation={presentation}
      goBackToParentView={props.goBackToParentView}
      onClose={props.onClose}
      closeAriaLabel={props.closeAriaLabel}
      sections={sections}
    />
  );
}
